import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { ApiResponse, DashboardOverview, DashboardPeriodType, JobRealtimeOverview, JobScheduleWorkersOverview } from '@/types'

interface DashboardOverviewFilters {
  type: DashboardPeriodType
  start?: string
  end?: string
}

const unwrapPayload = <T>(payload?: ApiResponse<T> | T | null): T | null => {
  if (!payload) {
    return null
  }

  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<T>).data ?? null
  }

  return payload as T
}

export interface MonthlyReportSummary {
  jobs_count: number
  cancelled_jobs_count: number
  invoice_total: number | string
  invoice_payment_total: number | string
}

export interface OrderMonthlyReportSummary {
  completed_count: number
  cancelled_count: number
  revenue: number
}

export interface MonthlyTrendPoint {
  month: string        // e.g. "Jan 25"
  completed: number
  cancelled: number
  revenue: number
}

export const dashboardApi = {
  getOverview: async (filters: DashboardOverviewFilters): Promise<DashboardOverview | null> => {
    const params = new URLSearchParams()
    params.append('type', filters.type)
    if (filters.start) params.append('start', filters.start)
    if (filters.end) params.append('end', filters.end)

    const { data } = await api.get<ApiResponse<DashboardOverview> | DashboardOverview>(API_PATHS.dashboard.summary, { params })
    return unwrapPayload<DashboardOverview>(data)
  },

  getRealtimeOverview: async (request?: { signal?: AbortSignal }): Promise<JobRealtimeOverview | null> => {
    const { data } = await api.get<ApiResponse<JobRealtimeOverview> | JobRealtimeOverview>(API_PATHS.dashboard.realtime, { signal: request?.signal })
    return unwrapPayload<JobRealtimeOverview>(data)
  },

  getScheduleWorkersOverview: async (start?: string, end?: string): Promise<JobScheduleWorkersOverview | null> => {
    const params = new URLSearchParams()
    if (start) params.append('start', start)
    if (end) params.append('end', end)

    const { data } = await api.get<ApiResponse<JobScheduleWorkersOverview> | JobScheduleWorkersOverview>(
      API_PATHS.dashboard.scheduleWorkers,
      { params }
    )
    return unwrapPayload<JobScheduleWorkersOverview>(data)
  },

  getJobsTrend: async (serviceInstanceId: string, months = 6): Promise<MonthlyTrendPoint[]> => {
    const now = new Date()
    const buckets: Array<{ month: number; year: number; label: string }> = []
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      buckets.push({
        month: d.getMonth() + 1,
        year: d.getFullYear(),
        label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      })
    }

    const results = await Promise.allSettled(
      buckets.map(({ month, year }) =>
        api.get<{ summary: MonthlyReportSummary }>(API_PATHS.reports.monthwise, {
          params: { month, year, service_instance_id: serviceInstanceId },
        })
      )
    )

    return buckets.map(({ label }, index) => {
      const result = results[index]
      if (result.status !== 'fulfilled') return { month: label, completed: 0, cancelled: 0, revenue: 0 }
      const summary = result.value.data?.summary
      return {
        month: label,
        completed: Number(summary?.jobs_count ?? 0),
        cancelled: Number(summary?.cancelled_jobs_count ?? 0),
        revenue: Number(summary?.invoice_payment_total ?? summary?.invoice_total ?? 0),
      }
    })
  },

  getOrdersTrend: async (serviceInstanceId: string, months = 6): Promise<MonthlyTrendPoint[]> => {
    const now = new Date()
    const buckets: Array<{ month: number; year: number; label: string }> = []
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      buckets.push({
        month: d.getMonth() + 1,
        year: d.getFullYear(),
        label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      })
    }

    const results = await Promise.allSettled(
      buckets.map(({ month, year }) =>
        api.get<{ summary: OrderMonthlyReportSummary }>(API_PATHS.reports.orderMonthwise, {
          params: { month, year, service_instance_id: serviceInstanceId },
        })
      )
    )

    return buckets.map(({ label }, index) => {
      const result = results[index]
      if (result.status !== 'fulfilled') return { month: label, completed: 0, cancelled: 0, revenue: 0 }
      const summary = result.value.data?.summary
      return {
        month: label,
        completed: Number(summary?.completed_count ?? 0),
        cancelled: Number(summary?.cancelled_count ?? 0),
        revenue: Number(summary?.revenue ?? 0),
      }
    })
  },
}